function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { withoutCompareFilters } from './cabinetCompareFilters';
import { normalizeSavedViewStyleCabinetFilters } from './cabinetSavedViewFilters';
export { normalizeSavedViewStyleCabinetFilters } from './cabinetSavedViewFilters';

/**
 * With hash history, query strings often live on the hash (`#/path?k=v`), not `location.search`.
 * Prefer `?...` from `location.hash` when present; otherwise fall back to `location.search`.
 *
 * @param {{ search?: string, hash?: string }} [location]
 * @returns {string}
 */
export const getCabinetLocationSearch = (location = {}) => {
  const hash = typeof location.hash === 'string' ? location.hash : '';
  const q = hash.indexOf('?');
  const hashQuery = q === -1 ? '' : hash.slice(q);
  // Prefer the in-app hash query so a stray document `?…` (e.g. SSO) cannot shadow cabinet
  // deep links that live on `#/cabinets/…?filters…`.
  if (hashQuery.length > 1) {
    return hashQuery;
  }
  if (typeof location.search === 'string' && location.search.length > 1) {
    return location.search;
  }
  return '';
};

/**
 * @param {string} [search]
 * @returns {{ filterId: string } | null}
 */
export const getCabinetSavedViewParamsFromSearch = search => {
  if (!search || typeof search !== 'string') {
    return null;
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('view') !== 'saved') {
    return null;
  }
  const filterId = params.get('filterId');
  if (!filterId || !String(filterId).trim()) {
    return null;
  }
  return {
    filterId: String(filterId).trim()
  };
};

/**
 * @param {{ search?: string, hash?: string }} [location]
 * @returns {boolean}
 */
export const hasCabinetSavedViewDeepLink = (location = {}) => {
  var _getCabinetSavedViewP;
  return Boolean((_getCabinetSavedViewP = getCabinetSavedViewParamsFromSearch(getCabinetLocationSearch(location))) === null || _getCabinetSavedViewP === void 0 ? void 0 : _getCabinetSavedViewP.filterId);
};

/**
 * User `content.savedViews[cabinetIdentifier][filterId]` row for saved-view URLs.
 *
 * @param {Record<string, unknown>} [savedViews]
 * @param {string} cabinetIdentifier
 * @param {string} filterId
 * @returns {Record<string, unknown> | null}
 */
export const resolveCabinetSavedViewRow = (savedViews, cabinetIdentifier, filterId) => {
  if (!savedViews || !cabinetIdentifier || !filterId) {
    return null;
  }
  const byFilter = savedViews[cabinetIdentifier];
  if (!byFilter || typeof byFilter !== 'object' || Array.isArray(byFilter)) {
    return null;
  }
  const direct = byFilter[filterId];
  if (direct && typeof direct === 'object') {
    return direct;
  }
  const keys = Object.keys(byFilter);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const row = byFilter[key];
    if (row && typeof row === 'object' && row.id != null && String(row.id) === String(filterId)) {
      return row;
    }
  }
  return null;
};

/**
 * Parse `location.search` from a hash route (e.g. `?projectTitle=Foo`) into cabinet
 * index `filters` entries. Only keys listed in `filterDefinitions[].index` are read;
 * other query params are ignored.
 *
 * Each query value becomes one cabinet filter selection in the same shape as user
 * saved views: `[[value], ...]`. Repeat the same key for multiple selections:
 * `?tag=a&tag=b`. Non-empty values are strings; empty values (`?tag=` or `…=`) become
 * `[null]` in the tuple, matching “No selection” in the cabinet filter UI.
 *
 * Also reads bracket params `filters[<index>][<row>][<col>]=…` (same shape as map / GET
 * URLs), e.g. `filters[locationTitle][0][0]=` for an explicit empty location filter.
 *
 * @param {string} [search]
 * @param {Array<{ index?: string }>} [filterDefinitions]
 * @returns {Record<string, Array<Array<string|null>>>}
 */
const FILTER_BRACKET_KEY_RE = /^filters\[([^\]]+)]\[(\d+)]\[(\d+)]$/;

/**
 * @param {URLSearchParams} params
 * @param {Set<string>} allowed
 * @returns {Record<string, Array<Array<string|null>>>}
 */
const getCabinetFiltersFromBracketSearchParams = (params, allowed) => {
  /** @type {Record<string, Map<number, Map<number, string>>>} */
  const byIndex = {};
  params.forEach((value, paramKey) => {
    const m = paramKey.match(FILTER_BRACKET_KEY_RE);
    if (!m) {
      return;
    }
    const idx = m[1];
    if (!allowed.has(idx)) {
      return;
    }
    const rowI = Number(m[2]);
    const colJ = Number(m[3]);
    if (!byIndex[idx]) {
      byIndex[idx] = new Map();
    }
    const rowMap = byIndex[idx];
    if (!rowMap.has(rowI)) {
      rowMap.set(rowI, new Map());
    }
    rowMap.get(rowI).set(colJ, value);
  });
  const out = {};
  Object.keys(byIndex).forEach(idx => {
    const rowMap = byIndex[idx];
    const sortedRows = [...rowMap.keys()].sort((a, b) => a - b);
    out[idx] = sortedRows.map(ri => {
      const colMap = rowMap.get(ri);
      const sortedCols = [...colMap.keys()].sort((a, b) => a - b);
      return sortedCols.map(cj => {
        const v = colMap.get(cj);
        return v === '' || v == null ? null : String(v);
      });
    });
  });
  return out;
};
export const getCabinetFiltersFromSearch = (search, filterDefinitions = []) => {
  if (!search || !filterDefinitions.length) {
    return {};
  }
  const allowed = new Set(filterDefinitions.map(f => f.index).filter(idx => typeof idx === 'string' && idx));
  if (!allowed.size) {
    return {};
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const out = getCabinetFiltersFromBracketSearchParams(params, allowed);
  allowed.forEach(key => {
    if (out[key]) {
      return;
    }
    const flatValues = params.getAll(key);
    if (!flatValues.length) {
      return;
    }
    out[key] = flatValues.map(v => [v === '' || v == null ? null : String(v)]);
  });
  return out;
};

/**
 * Builds `filters` the same way selecting a saved cabinet view does: clear full-text
 * and AI search, reset `metaDeleted`, drop prior `filterDefinitions` selections, then
 * apply `savedStyleFilters` (from a saved view blob or from deep-link parsing).
 *
 * @param {{
 *   existingFilters?: Record<string, unknown>,
 *   savedStyleFilters?: Record<string, unknown>,
 *   formTypeIdentifiers: string[],
 *   cabinetIdentifier: string,
 *   filterDefinitions?: Array<{ index?: string }>,
 * }} args
 * @returns {Record<string, unknown>}
 */
export const mergeCabinetSavedStyleFilters = ({
  existingFilters: _existingFilters = {},
  savedStyleFilters: _savedStyleFilters = {},
  formTypeIdentifiers,
  cabinetIdentifier,
  filterDefinitions: _filterDefinitions = []
}) => {
  var _existingFilters$form;
  const defIndexes = new Set(_filterDefinitions.map(f => f.index).filter(idx => typeof idx === 'string' && idx));
  const normalizedSaved = normalizeSavedViewStyleCabinetFilters(_savedStyleFilters);
  const next = _objectSpread(_objectSpread({}, withoutCompareFilters(_existingFilters)), {}, {
    text: '',
    aiSearch: '',
    // Match handleClear / saved-view apply: always reset deleted visibility.
    metaDeleted: 0
  });
  defIndexes.forEach(idx => {
    if (next[idx] !== undefined) {
      delete next[idx];
    }
  });
  return _objectSpread(_objectSpread({}, next), {}, {
    formTypeIdentifiers: (_existingFilters$form = _existingFilters.formTypeIdentifiers) !== null && _existingFilters$form !== void 0 ? _existingFilters$form : formTypeIdentifiers,
    cabinetIdentifier
  }, normalizedSaved);
};

/**
 * Build the index query object passed to `cabinetActions.serverReplaceView` — the same
 * route used when applying a saved cabinet filter. Deep links parse into `savedView.filters`;
 * saved rows pass `savedView.filters` and `savedView.sort` from storage.
 *
 * @param {{
 *   indexQuery?: Record<string, unknown>,
 *   initialIndexQuery?: Record<string, unknown>,
 *   savedView: { filters?: Record<string, unknown>, sort?: unknown },
 *   formTypeIdentifiers: string[],
 *   cabinetIdentifier: string,
 *   filterDefinitions?: Array<{ index?: string }>,
 *   mergeExistingCabinetFilters?: boolean,
 * }} args
 * @returns {Record<string, unknown>}
 */
export const buildCabinetReplaceViewQuery = ({
  indexQuery,
  initialIndexQuery: _initialIndexQuery = {},
  savedView,
  formTypeIdentifiers,
  cabinetIdentifier,
  filterDefinitions: _filterDefinitions2 = [],
  mergeExistingCabinetFilters: _mergeExistingCabinetFilters = true
}) => {
  const base = indexQuery && Object.keys(indexQuery).length ? indexQuery : _objectSpread({}, _initialIndexQuery);
  const existingForMerge = _mergeExistingCabinetFilters ? base.filters || {} : {};
  const mergedFilters = mergeCabinetSavedStyleFilters({
    existingFilters: existingForMerge,
    savedStyleFilters: savedView.filters || {},
    formTypeIdentifiers,
    cabinetIdentifier,
    filterDefinitions: _filterDefinitions2
  });
  const sort = savedView.sort !== undefined ? savedView.sort : base.sort;
  return _objectSpread(_objectSpread({}, base), {}, {
    filters: mergedFilters,
    sort
  });
};

/**
 * Build the `serverReplaceView` payload from the URL only. Does not read Redux
 * `indexQuery.filters` — same idea as a fresh saved-filter apply (no prior cabinet
 * selections merged in). Paging/sort come from `initialIndexQuery` only.
 *
 * @param {{
 *   location: { search?: string, hash?: string },
 *   filterDefinitions: Array<{ index?: string }>,
 *   initialIndexQuery?: Record<string, unknown>,
 *   formTypeIdentifiers?: string[],
 *   cabinetIdentifier: string,
 * }} args
 * @returns {{
 *   hasUrlFilters: boolean,
 *   replaceViewQuery: Record<string, unknown> | null,
 *   deepLinkKey: string,
 * }}
 */
export const getCabinetDeepLinkReplaceViewPayload = ({
  location,
  filterDefinitions,
  initialIndexQuery: _initialIndexQuery2 = {},
  formTypeIdentifiers: _formTypeIdentifiers = [],
  cabinetIdentifier,
  savedViews
}) => {
  const locationSearch = getCabinetLocationSearch(location);
  const savedViewParams = getCabinetSavedViewParamsFromSearch(locationSearch);
  if (savedViewParams !== null && savedViewParams !== void 0 && savedViewParams.filterId) {
    const deepLinkKey = `${cabinetIdentifier}|saved|${savedViewParams.filterId}`;
    const row = resolveCabinetSavedViewRow(savedViews, cabinetIdentifier, savedViewParams.filterId);
    if (row) {
      return {
        hasUrlFilters: true,
        replaceViewQuery: buildCabinetReplaceViewQuery({
          indexQuery: undefined,
          initialIndexQuery: _initialIndexQuery2,
          savedView: row,
          formTypeIdentifiers: _formTypeIdentifiers,
          cabinetIdentifier,
          filterDefinitions,
          mergeExistingCabinetFilters: false
        }),
        deepLinkKey
      };
    }
    return {
      hasUrlFilters: true,
      replaceViewQuery: null,
      deepLinkKey
    };
  }
  const searchFilterPatch = getCabinetFiltersFromSearch(locationSearch, filterDefinitions);
  const hasUrlFilters = Object.keys(searchFilterPatch).length > 0;
  const replaceViewQuery = hasUrlFilters ? buildCabinetReplaceViewQuery({
    indexQuery: undefined,
    initialIndexQuery: _initialIndexQuery2,
    savedView: {
      filters: searchFilterPatch
    },
    formTypeIdentifiers: _formTypeIdentifiers,
    cabinetIdentifier,
    filterDefinitions,
    mergeExistingCabinetFilters: false
  }) : null;
  const deepLinkKey = hasUrlFilters ? `${cabinetIdentifier}|${locationSearch}` : '';
  return {
    hasUrlFilters,
    replaceViewQuery,
    deepLinkKey
  };
};

/**
 * True while Redux `currentCabinet` has not caught up to the URL cabinet, but the hash
 * already contains at least one known cabinet filter param. In that window we must not
 * call bare `serverIndex` or we GET /forms twice (bare load, then deep link).
 */
export const shouldDeferBareCabinetIndexForDeepLink = ({
  location,
  filterDefinitions,
  currentCabinet,
  urlCabinetIdentifier,
  updatedFormType,
  formTypeIdentifiers: _formTypeIdentifiers2 = []
}) => {
  if (!_formTypeIdentifiers2.length || currentCabinet === urlCabinetIdentifier || updatedFormType) {
    return false;
  }
  if (hasCabinetSavedViewDeepLink(location)) {
    return true;
  }
  return Object.keys(getCabinetFiltersFromSearch(getCabinetLocationSearch(location), filterDefinitions)).length > 0;
};
export default getCabinetFiltersFromSearch;